// Copyright 2024 Sorama B.V.

import { CriticalBandUnit, UnitType } from "units/Unit";

/**
 * Represents a critical band unit (26 critical bands)
 */
export class CriticalBand extends UnitType {
    public static override typeName = "Critical Band";

    public static averaged: CriticalBandUnit = new CriticalBandUnit(
        "",
        this.criticalBand26ToHz,
        this.hzToCriticalBand26,
        this.subBandToCriticalBand,
        CriticalBand,
        { unitFamily: CriticalBand }
    );
    /**
     * Converts a critical band number to its corresponding frequency range in Hz.
     * @param criticalBandValue The critical band number (0 to 26.5).
     * @returns The frequency in Hz.
     * @throws Error if the critical band number is not between 0 and 26.5.
     * @remarks
     * This critical band scale is used for Roughness, Fluctuation Strength, and Tonality
     */

    public static criticalBand26ToHz(criticalBandValue: number): number {
        const deltaFf0 = 81.9289;
        const c = 0.1618;

        if (criticalBandValue < 0 || criticalBandValue > 26.5) {
            throw new Error("Critical band number must be a value between 0 and 26");
        }

        return (deltaFf0 / c) * Math.sinh(c * criticalBandValue);
    }

    /**
     * Converts a frequency in Hz to the corresponding critical band number.
     * @param frequency positive frequency
     * @returns The corresponding critical band number for frequencies between 0 and 18428 Hz. Values above 18428 Hz will return 26.5
     * @throws Error if frequency is negative
     * @remarks
     * This critical band scale is used for Roughness, Fluctuation Strength, and Tonality
     */
    public static hzToCriticalBand26(frequency: number): number {
        const deltaFf0 = 81.9289;
        const c = 0.1618;

        if (frequency < 0) {
            throw new Error("Frequency must be a positive number");
        }

        const inverseSinh = (x: number) => Math.log(x + Math.sqrt(x * x + 1));

        let band = inverseSinh((frequency * c) / deltaFf0) / c;

        band = Math.min(band, 26.5);

        return band;
    }

    /**
     * Converts a critical band subband index to its corresponding critical band number.
     * Every 2 subbands equals to 1 full band
     * The subbands are defined from index 1
     * And each band should be spaced around its full band value equally, so
     * [1,2] => [0.75,1.25]
     * [9,10] => [4.75,5.25]
     * @param subBandIndex The subband index (1 to 53).
     * @returns The corresponding critical band number.
     */
    public static subBandToCriticalBand(subBand: number): number {
        const subBandsPerBand = 2;
        // The zero based index of the subband that should be in the middle, or exactly on the band value
        // if the number of subbands is odd it will be an index (in this case) of 0.5, it's the middle of [0,1]
        const middleOfSubBands = (subBandsPerBand - 1) / 2;

        return (subBand + middleOfSubBands) / subBandsPerBand;
    }
}
